import React from 'react';
import { withRouter } from "react-router";
import { connect } from "react-redux";
import { API } from 'aws-amplify';
import _ from "lodash";
import { CircularProgress } from "@mui/material";

import {
    fetchAllTasks,
    fetchAllTaskTypes,
    postTask,
    putTask,
    postTaskType,
    fetchAllTaskActionTypes
} from "@/shared/tasks/actions";
import { startConversation } from "@/shared/conversation/actions";
import { fetchAllWorkflows } from "@/shared/workflow/actions";
import { useQuery } from "@/utils/utils";

import TasksOverviewList from "./TasksOverviewList";
import AddTask from "./AddTask";

const mapStateToProps = state => ({
    ...state
})

const mapDispatchToProps = dispatch => ({
    getTasks: (status, assignee, search, taskType, page, append = false) => dispatch(fetchAllTasks(status, assignee, search, taskType, page, append)),
    getTaskTypes: () => dispatch(fetchAllTaskTypes()),
    addNewTask: (newTask, status, assignee, taskType) => dispatch(postTask(newTask, status, assignee, taskType)),
    updateTask: (updatedTask, status, assignee, taskType) => dispatch(putTask(updatedTask, status, assignee, taskType)),
    addNewTaskType: (newTaskType) => dispatch(postTaskType(newTaskType)),
    loadWorkflows: () => dispatch(fetchAllWorkflows()),
    startConversationFromTask: (workflowId, phoneNumber, encounter) => dispatch(startConversation(workflowId, phoneNumber, encounter)),
    fetchTaskActionTypes: () => dispatch(fetchAllTaskActionTypes())
})

function TasksIndex({ ...props }) {
    const query = useQuery();
    const tabParam = props.match?.params?.tab;
    const taskIdParam = query.get('taskId');
    const openTaskParam = query.get('openTask');
    const tabs = ['OPEN', 'IN_PROGRESS', 'OVERDUE', 'CLOSED'];
    const initialTab = tabParam && tabs.includes(tabParam) ? tabParam : 'OPEN';

    const initialAutoOpenTaskId = openTaskParam === '1' && taskIdParam ? taskIdParam : null;

    const [page, setPage] = React.useState(0);
    const [taskType, setTaskType] = React.useState('');
    const [activeTab, setActiveTab] = React.useState(initialTab);
    const [assignee, setAssignee] = React.useState(
        initialAutoOpenTaskId
            ? ''
            : (props.user.aboutMe.user ? props.user.aboutMe.user.id : '')
    );
    const [isLoading, setIsLoading] = React.useState(true);
    const [announcement, setAnnouncement] = React.useState('');
    const [deepLinkedTask, setDeepLinkedTask] = React.useState(null);
    const [autoOpenTaskId, setAutoOpenTaskId] = React.useState(initialAutoOpenTaskId);

    const tabsRef = React.useRef([]);
    const awaitingData = React.useRef(true);

    const [customFilters, setCustomFilters] = React.useState({
        'assignee': {
            displayName: 'Assignee',
            selected: false,
            placeholder: 'Search by assignee',
            options: [],
            onChange: e => {
                setAssignee(e.target.value);
                setPage(0);
            },
            clear: () => {
                setAssignee(null);
                setPage(0);
            },
            value: assignee,
        },
        'taskType': {
            displayName: 'Task type',
            selected: false,
            placeholder: 'Search by task type',
            options: props?.tasks?.taskTypes?.map(t => { return { key: t.id, value: t.name } }),
            onChange: e => {
                setTaskType(e.target.value);
                setPage(0);
            },
            clear: () => {
                setTaskType(null);
                setPage(0);
            },
            value: taskType,
        }
    });

    React.useEffect(() => {
        props.getTaskTypes();
        props.loadWorkflows();
        props.fetchTaskActionTypes();
        fetchAssignees();
        if (autoOpenTaskId) {
            API.get('Core', '/api/v1/task/' + autoOpenTaskId)
                .then(task => setDeepLinkedTask(task))
                .catch(() => setAutoOpenTaskId(null));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    React.useEffect(() => {
        setCustomFilters(prev => ({
            ...prev,
            taskType: {
                ...prev.taskType,
                options: props?.tasks?.taskTypes?.map(t => ({ key: t.id, value: t.name }))
            }
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.tasks.taskTypes])

    React.useEffect(() => {
        const shouldAppend = page > 0;
        awaitingData.current = true;
        setIsLoading(true);
        props.getTasks(activeTab, assignee, null, taskType, page, shouldAppend);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, assignee, taskType, page])

    // Set loading to false when data arrives
    React.useEffect(() => {
        if (awaitingData.current && props.tasks.tasks.content !== undefined) {
            awaitingData.current = false;
            setIsLoading(false);
            // Announce data load completion for screen readers
            const label = getStatusLabel(activeTab);
            const count = props.tasks.tasks.content?.length || 0;
            const total = props.tasks.tasks.totalElements || 0;
            setAnnouncement(`Data loading finished, showing ${count} of ${total} ${label} tasks`);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.tasks.tasks.content, props.tasks.tasks.totalElements])

    // Clear loading state on fetch failure so spinner doesn't hang
    React.useEffect(() => {
        if (props.tasks.tasksLoadFailed) {
            awaitingData.current = false;
            setIsLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.tasks.tasksLoadFailed])

    const handlePageChange = (event, page) => setPage(page - 1);

    const getStatusLabel = (status) => {
        const labels = {
            'OVERDUE': 'Overdue',
            'OPEN': 'Upcoming',
            'IN_PROGRESS': 'In Progress',
            'CLOSED': 'Closed'
        };
        return labels[status] || '';
    };

    const loadMore = () => {
        setPage(prevPage => prevPage + 1);
    };

    const handleTabChange = (tabStatus) => {
        setActiveTab(tabStatus);
        setPage(0);
    };

    const handleKeyDown = (event, currentIndex) => {
        let newIndex = currentIndex;

        if (event.key === 'ArrowRight') {
            event.preventDefault();
            newIndex = (currentIndex + 1) % tabs.length;
        } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        } else if (event.key === 'Home') {
            event.preventDefault();
            newIndex = 0;
        } else if (event.key === 'End') {
            event.preventDefault();
            newIndex = tabs.length - 1;
        } else {
            return;
        }
        tabsRef.current[newIndex]?.focus();
        //NVDA double announcement issue
        requestAnimationFrame(() => {
            handleTabChange(tabs[newIndex]);
        });
    };

    const fetchAssignees = () => {
        API.get('Core', '/api/v1/users/provider')
            .then(providers => {
                setCustomFilters(prev => ({
                    ...prev,
                    assignee: {
                        ...prev.assignee,
                        options: providers.map(p => ({ key: p.id, value: `${p.firstName} ${p.lastName}` }))
                    }
                }));
            })
    }

    const onConversationStarted = (workflow, number, encounter) => {
        props.startConversationFromTask(workflow.id, number, encounter);
        const win = window.open('/conversations/workflow/' + workflow.id + '/conversations/', "_blank");
        win.focus();
    }

    const onSourceConversationView = (workflow, conversation) => {
        const win = window.open('/conversations/workflow/' + workflow.id + '/conversations/' + conversation.id, "_blank");
        win.focus();
    }

    const onAddTaskSubmit = (newTask) => {
        props.addNewTask(newTask, activeTab, assignee, taskType);
    }

    const onUpdateTaskSubmit = (updateTask) => {
        props.updateTask(updateTask, activeTab, assignee, taskType);
    }

    return (
        <div className="rootView tasksView">
            <div className="navBar">
                <div className="navTitle">
                    <div className="title">Tasks</div>
                    <div className="counterBadge">
                        <div className="counterValue">{props.tasks.tasks.totalElements || 0}</div>
                    </div>
                </div>

                <div className="actions">
                    {
                        Object.keys(customFilters).filter(k => customFilters[k].options?.length).map((f, i) => {
                            return (<div key={`tasksCustomFilter${i}`} className={`searchInput action `}>

                                <select
                                    className='taskCustomFilterSelect'
                                    data-uw-rm-ignore
                                    placeholder={customFilters[f]?.placeholder}
                                    onChange={e => customFilters[f].onChange(e)}
                                    value={f === 'assignee' ? assignee : taskType}
                                    aria-label={customFilters[f]?.displayName}
                                    tabIndex={0}>
                                    <option data-uw-rm-ignore value='' label='ALL'>ALL</option>
                                    {customFilters[f]?.options?.map((o, j) => <option data-uw-rm-ignore key={`tasksCustomFilter${i}-option${j}`} label={o.value} value={o.key}>{o.value}</option>)}
                                </select>

                            </div>);
                        })
                    }
                    <AddTask props={props} taskTypes={props.tasks.taskTypes} onAdd={onAddTaskSubmit}>
                        <div
                            className="add action"
                            role="button"
                            tabIndex={0}
                            aria-label="Add new task"
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}>
                            <div className="icon" />
                            <div className="title">Add Task</div>
                        </div>
                    </AddTask>
                </div>

                <div className="separator"></div>
            </div>

            {/* Tab Navigation - Below navbar */}
            <div className="taskTabsSection">
                <div className={`segmentedBar index${_.indexOf(tabs, activeTab)}`} style={{ width: "420px", maxWidth: "100%" }} role="tablist" aria-label="Task status">
                    {
                        _.map(tabs, function (t, tk) {
                            return (
                                <div
                                    key={tk}
                                    id={`tab-${t}`}
                                    ref={el => tabsRef.current[tk] = el}
                                    onClick={() => handleTabChange(t)}
                                    onKeyDown={(e) => handleKeyDown(e, tk)}
                                    className={`segment ${t === activeTab ? 'selected' : ''}`}
                                    role="tab"
                                    aria-selected={t === activeTab}
                                    aria-controls="tasks-tabpanel"
                                    tabIndex={t === activeTab ? 0 : -1}
                                    aria-label={`${getStatusLabel(t)} tasks`}>
                                    <div className="title" aria-hidden="true">{getStatusLabel(t)}</div>
                                </div>
                            );
                        })
                    }
                    <div className="selection" style={{ width: (100 / tabs.length) + '%' }} />
                </div>
            </div>

            {/* Screen reader announcements - 508c compliant */}
            <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                className="sr-only">
                {announcement}
            </div>

            <div className="scrollView" id="tasks-tabpanel" role="tabpanel" aria-labelledby={`tab-${activeTab}`} aria-busy={isLoading}>
                {isLoading && page === 0 && (
                    <div className="loadingSpinnerContainer">
                        <CircularProgress
                            role="status"
                            aria-label="Loading tasks"
                            aria-live="polite"
                        />
                    </div>
                )}
                {(page === 0 ? !isLoading : true) && (
                    <>
                <TasksOverviewList
                    props={props}
                    onChange={onUpdateTaskSubmit}
                    onConversationStarted={onConversationStarted}
                    onViewSourceConversation={onSourceConversationView}
                    handlePageChange={handlePageChange}
                    currentPage={page}
                    totalPages={props?.tasks?.totalPages}
                    activeTab={activeTab}
                    isLoading={isLoading}
                    autoOpenTaskId={autoOpenTaskId}
                    onAutoOpenConsumed={() => setAutoOpenTaskId(null)}
                    deepLinkedTask={deepLinkedTask}
                />

                {/* Show More Button or Loading Spinner */}
                {
                    (props.tasks.tasks && !props.tasks.tasks.last) ? (
                        isLoading && page > 0 ? (
                            <div className="loadMoreSpinnerContainer">
                                <CircularProgress
                                    size={30}
                                    role="status"
                                    aria-label="Loading more tasks"
                                />
                            </div>
                        ) : (
                            <div onClick={loadMore} className="encountersExpander" role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); loadMore(); } }} aria-label="show more tasks">
                                <div className="expanderButton">
                                    <div className="title">Show More</div>
                                    <div className="icon" />
                                </div>
                            </div>
                        )
                    ) : null
                }
                    </>
                )}
            </div>
        </div>
    )
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(TasksIndex));
